import React from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { FiTrash2 } from "react-icons/fi";

/**
 * ConfirmDialog — reusable confirmation modal.
 */
const ConfirmDialog = ({
    title,
    message,
    confirmText = "Yes",
    cancelText = "No, Go Back",
    variant = "primary",
    showIcon = false,
    footnote,
    onConfirm,
    onCancel,
}) => {
    return ReactDOM.createPortal(
        <Overlay>
            <Content>
                {showIcon && (
                    <IconWrapper>
                        <OuterCircle>
                            <InnerCircle>
                                <FiTrash2 size={32} color="#ef4444" />
                            </InnerCircle>
                        </OuterCircle>
                    </IconWrapper>
                )}
                <Title>{title}</Title>
                <Message>{message}</Message>
                <Buttons>
                    <CancelBtn onClick={onCancel}>{cancelText}</CancelBtn>
                    <ConfirmBtn $variant={variant} onClick={onConfirm}>{confirmText}</ConfirmBtn>
                </Buttons>
                {footnote && <Footnote>{footnote}</Footnote>}
            </Content>
        </Overlay>,
        document.body
    );
};

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const Content = styled.div`
    background: white;
    border-radius: 1.5rem;
    padding: 2rem;
    text-align: center;
    width: 90%;
    max-width: 28rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
`;

const IconWrapper = styled.div`
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: center;
`;

const OuterCircle = styled.div`
    width: 4rem;
    height: 4rem;
    background-color: #fee2e2;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    animation: pulse 1.5s infinite;

    @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.7; }
    }
`;

const InnerCircle = styled.div`
    width: 3rem;
    height: 3rem;
    background-color: #fca5a5;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Title = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.5rem;
`;

const Message = styled.p`
    color: #4b5563;
    line-height: 1.5;
    margin-bottom: 2rem;
    font-size: 0.95rem;

    strong {
        font-weight: 600;
        color: #111827;
    }
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    @media (min-width: 640px) {
        flex-direction: row;
    }
`;

const CancelBtn = styled.button`
    flex: 1;
    padding: 11px 22px;
    border-radius: 9999px;
    background-color: #f3f4f6;
    color: #374151;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
        background-color: #e5e7eb;
    }
    &:active {
        transform: scale(0.95);
    }
`;

const variantMap = {
    danger:  { bg: "#ef4444", hover: "#dc2626" },
    primary: { bg: "#9333ea", hover: "#7c3aed" },
};

const ConfirmBtn = styled.button`
    flex: 1;
    padding: 11px 22px;
    border-radius: 9999px;
    background-color: ${({ $variant = "primary" }) => variantMap[$variant].bg};
    color: white;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
        background-color: ${({ $variant = "primary" }) => variantMap[$variant].hover};
    }
    &:active {
        transform: scale(0.95);
    }
    &:focus-visible {
        outline: 2px solid ${({ $variant = "primary" }) => variantMap[$variant].bg};
        outline-offset: 2px;
    }
`;

const Footnote = styled.p`
    margin-top: 1.5rem;
    font-size: 0.75rem;
    color: #7c828c;
    font-style: italic;
`;

export default ConfirmDialog;
